import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import he from 'he';
import { getAvatarComponent, getAvatarPath } from '../utils/avatarHelper';
import './UserProfile.css';
import api from '../utils/api';

const UserProfile = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ avatar_preset: null, bio: '' });
  const [saving, setSaving] = useState(false);
  
  const avatarPresets = ['doge1', 'doge2', 'doge3', 'doge4', 'doge5', 'doge6', 'doge7', 'doge8'];

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/users/${id}`);
      setProfile(response.data.user);
      setStats(response.data.stats);
      setEditForm({
        avatar_preset: response.data.user.avatar_preset || null,
        bio: response.data.user.bio || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      if (error.response?.status === 404) {
        alert('User not found');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      avatar_preset: profile?.avatar_preset || null,
      bio: profile?.bio || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Update avatar preset if changed
      if (editForm.avatar_preset !== profile?.avatar_preset) {
        if (editForm.avatar_preset) {
          await api.put('/api/auth/avatar', { avatar_preset: editForm.avatar_preset });
        }
      }
      
      // Update bio
      await api.put(`/api/users/${id}/profile`, { bio: editForm.bio });
      
      // Reload profile to get updated data
      await loadProfile();
      
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isOwnProfile = currentUser && currentUser.id === parseInt(id);

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-window win98-bevel">
          <div className="loading-text">Loading profile... Much Wow! <img src="/doge.png" alt="Doge" className="doge-inline-image" /></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <div className="profile-window win98-bevel">
          <div className="error-text">User not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-window win98-bevel">
        <div className="navigation-bar win98-bevel-inset">
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              💬 Chat
            </Link>
            <Link to="/puddle" className={`nav-link ${location.pathname === '/puddle' ? 'active' : ''}`}>
              💎 Puddle
            </Link>
            <Link to="/leaderboard" className={`nav-link ${location.pathname === '/leaderboard' ? 'active' : ''}`}>
              🏆 Leaderboard
            </Link>
            <Link to="/settings" className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`}>
              ⚙️ Settings
            </Link>
            <Link to="/faq" className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`}>
              ❓ FAQ
            </Link>
            <Link to="/tos" className={`nav-link ${location.pathname === '/tos' ? 'active' : ''}`}>
              📜 Rules/ToS
            </Link>
            <a 
              href="https://discord.gg/B26JbHZsqq" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-link discord-link"
              title="Join our Discord"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0824 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0824 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 49.9718 48.5383C51.038 50.6034 52.2554 52.5699 53.5951 54.435C53.651 54.5139 53.7489 54.5477 53.8413 54.5195C59.646 52.7249 65.5286 50.0174 71.6015 45.5576C71.6551 45.5182 71.6887 45.459 71.6943 45.3942C73.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.2991 34.8216 17.2991 31.6709C17.2991 28.5203 20.1717 26.0166 23.7259 26.0166C27.308 26.0166 30.1626 28.4935 30.1526 31.6709C30.1426 34.8483 27.308 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.8911 34.8216 40.8911 31.6709C40.8911 28.5203 43.7636 26.0166 47.3178 26.0166C50.9 26.0166 53.7545 28.4935 53.7446 31.6709C53.7346 34.8483 50.9 37.3253 47.3178 37.3253Z" fill="#5865F2"/>
              </svg>
              Discord
            </a>
          </div>
        </div>

        <div className="profile-header">
          <h1 className="profile-title">{he.encode(profile.username)}'s Profile</h1>
          {isOwnProfile && !isEditing && (
            <button onClick={handleEdit} className="win98-button edit-button">
              ✏️ Edit Profile
            </button>
          )}
        </div>

        <div className="profile-content">
          <div className="profile-sidebar win98-bevel-inset">
            <div className="avatar-container">
              {getAvatarComponent(profile.avatar_preset, 150)}
            </div>
            {profile.badge === 'top_winner' && (
              <div className="badge-container">
                <div className="badge top-winner-badge">
                  <span className="badge-icon">👑</span>
                  <span className="badge-text">Top Winner</span>
                </div>
                <p className="badge-description">Highest upvoted winner in the last 7 days!</p>
              </div>
            )}
            {isEditing ? (
              <div className="edit-avatar" style={{ marginTop: '20px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  Choose Avatar:
                </label>
                <div className="avatar-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px',
                  marginBottom: '10px'
                }}>
                  {avatarPresets.map((preset) => {
                    const avatarPath = getAvatarPath(preset);
                    const isSelected = editForm.avatar_preset === preset;
                    return (
                      <div
                        key={preset}
                        onClick={() => setEditForm({ ...editForm, avatar_preset: preset })}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          border: isSelected ? '3px solid #0066cc' : '2px solid #000',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          background: isSelected ? '#e6f2ff' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          boxShadow: isSelected ? '0 0 8px rgba(0, 102, 204, 0.5)' : 'none'
                        }}
                        title={preset}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.background = '#f0f0f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.background = '#fff';
                          }
                        }}
                      >
                        {avatarPath ? (
                          <img
                            src={avatarPath}
                            alt={preset}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '2px'
                            }}
                            onError={(e) => {
                              if (e.target && e.target.style) {
                                e.target.style.display = 'none';
                              }
                              if (e.target && e.target.parentElement) {
                                e.target.parentElement.innerHTML = '🐕';
                                if (e.target.parentElement.style) {
                                  e.target.parentElement.style.fontSize = '1.5rem';
                                }
                              }
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '1.5rem' }}>🐕</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '0.85rem', 
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: editForm.avatar_preset ? 'normal' : 'italic'
                }}>
                  {editForm.avatar_preset ? `Selected: ${editForm.avatar_preset}` : 'Click an avatar to select'}
                </div>
              </div>
            ) : null}
          </div>

          <div className="profile-main">
            <div className="profile-section win98-bevel-inset">
              <h2 className="section-title">About Me</h2>
              {isEditing ? (
                <div className="edit-bio">
                  <label className="form-label">Bio:</label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    className="form-textarea win98-bevel-inset"
                    placeholder="Tell us about yourself..."
                    maxLength={500}
                    rows={5}
                  />
                  <div className="char-count">{editForm.bio.length}/500</div>
                </div>
              ) : (
                <div className="bio-content">
                  {profile.bio ? (
                    <p className="bio-text">{he.encode(profile.bio)}</p>
                  ) : (
                    <p className="bio-empty">No bio yet. {isOwnProfile && 'Click "Edit Profile" to add one!'}</p>
                  )}
                </div>
              )}
            </div>

            <div className="profile-section win98-bevel-inset">
              <h2 className="section-title">📊 Stats</h2>
              <div className="stats-grid">
                <div className="stat-box">
                  <div className="stat-icon">💬</div>
                  <div className="stat-info">
                    <div className="stat-label">Messages Sent</div>
                    <div className="stat-value">{stats?.total_messages || 0}</div>
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-icon">⬆️</div>
                  <div className="stat-info">
                    <div className="stat-label">Upvotes Received</div>
                    <div className="stat-value">{stats?.total_upvotes_received || 0}</div>
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-info">
                    <div className="stat-label">Daily Wins</div>
                    <div className="stat-value">{stats?.daily_wins || 0}</div>
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-icon">📅</div>
                  <div className="stat-info">
                    <div className="stat-label">Member Since</div>
                    <div className="stat-value">{formatDate(profile.member_since)}</div>
                  </div>
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="edit-actions">
                <button
                  onClick={handleSave}
                  className="win98-button save-button"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  className="win98-button cancel-button"
                  disabled={saving}
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="profile-footer">
          <button onClick={() => navigate(-1)} className="win98-button back-button">
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;

